import { DialogComponent } from '@theme/dialog';

const STORAGE_KEY = 'privacy-cookie-consent';

/**
 * Optional cookie category IDs (can be toggled).
 * Strictly necessary is always on and excluded.
 */
const OPTIONAL_CATEGORIES = ['performance', 'functional', 'targeting', 'chat'];

/**
 * @typedef {Record<string, boolean>} ConsentState
 */

/**
 * Reads consent state from localStorage.
 * @returns {ConsentState}
 */
export function getConsentState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Saves consent state to localStorage.
 * @param {ConsentState} state
 */
export function saveConsentState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    document.dispatchEvent(new CustomEvent('privacy:consent-updated', { detail: { state } }));
  } catch {
    // ignore
  }
}

/**
 * Returns state with all optional categories disabled.
 * @returns {ConsentState}
 */
export function getRejectAllState() {
  const state = {};
  for (const id of OPTIONAL_CATEGORIES) {
    state[id] = false;
  }
  return state;
}

/**
 * Returns state with all optional categories enabled.
 * @returns {ConsentState}
 */
export function getAllowAllState() {
  const state = {};
  for (const id of OPTIONAL_CATEGORIES) {
    state[id] = true;
  }
  return state;
}

/**
 * Privacy Preference Center drawer.
 * Uses shared DialogComponent shell: overlay, ESC, click-outside, X.
 * Slides in from right, same timing as menu submenu (0.3s, cubic-bezier(0.33, 0, 0.2, 1)).
 *
 * @extends {DialogComponent}
 */
class PrivacyPreferenceDrawerComponent extends DialogComponent {
  requiredRefs = ['dialog', 'content', 'confirmBtn', 'rejectBtn'];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(PrivacyDrawerOpenEvent.eventName, this.#handleOpen);
    window.openPrivacyPreferenceCenter = () => this.open();

    const { confirmBtn, rejectBtn } = this.refs;
    if (confirmBtn) confirmBtn.addEventListener('click', this.#handleConfirm);
    if (rejectBtn) rejectBtn.addEventListener('click', this.#handleRejectAll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(PrivacyDrawerOpenEvent.eventName, this.#handleOpen);
    if (window.openPrivacyPreferenceCenter === this.open) {
      delete window.openPrivacyPreferenceCenter;
    }

    const { confirmBtn, rejectBtn } = this.refs;
    if (confirmBtn) confirmBtn.removeEventListener('click', this.#handleConfirm);
    if (rejectBtn) rejectBtn.removeEventListener('click', this.#handleRejectAll);
  }

  /** @param {PrivacyDrawerOpenEvent} _event */
  #handleOpen = () => {
    this.open();
  };

  open() {
    if (this.refs.dialog?.open) return;

    this.#syncTogglesFromStorage();
    this.showDialog();
    this.dispatchEvent(new CustomEvent('privacy-drawer:open', { bubbles: true }));
  }

  /**
   * Syncs toggle checkboxes from localStorage consent state.
   */
  #syncTogglesFromStorage() {
    const state = getConsentState();
    for (const id of OPTIONAL_CATEGORIES) {
      const input = this.querySelector(`[data-cookie-category="${id}"]`);
      if (input instanceof HTMLInputElement) {
        input.checked = state[id] !== false;
      }
    }
  }

  /**
   * Collects current toggle state from the DOM.
   * @returns {ConsentState}
   */
  #getCurrentState() {
    const state = {};
    for (const id of OPTIONAL_CATEGORIES) {
      const input = this.querySelector(`[data-cookie-category="${id}"]`);
      state[id] = input instanceof HTMLInputElement ? input.checked : false;
    }
    return state;
  }

  #handleConfirm = () => {
    const state = this.#getCurrentState();
    saveConsentState(state);
    this.closeDialog();
    this.dispatchEvent(new CustomEvent('privacy:confirmed', { detail: { state }, bubbles: true }));
  };

  #handleRejectAll = () => {
    const state = getRejectAllState();
    saveConsentState(state);
    this.#syncTogglesFromStorage();
    this.closeDialog();
    this.dispatchEvent(new CustomEvent('privacy:rejected', { detail: { state }, bubbles: true }));
  };
}

if (!customElements.get('privacy-preference-drawer-component')) {
  customElements.define('privacy-preference-drawer-component', PrivacyPreferenceDrawerComponent);
}

/**
 * Dispatched to open the privacy preference center.
 * @example document.dispatchEvent(new PrivacyDrawerOpenEvent());
 */
export class PrivacyDrawerOpenEvent extends CustomEvent {
  constructor() {
    super(PrivacyDrawerOpenEvent.eventName);
  }

  static eventName = 'privacy-drawer:open';
}

export { PrivacyPreferenceDrawerComponent };
